import React, {
    ButtonHTMLAttributes,
    forwardRef,
    InputHTMLAttributes,
    ReactNode,
    useCallback,
    useState,
} from 'react';
import cn from 'classnames';
import { Icon } from '../icon/icon';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
    /**
     * Unique ID for the text field
     */
    id: string;
    /**
     * Label inside the text field, can also contain React elements
     */
    label?: ReactNode;
    /**
     * Type of the text field
     */
    type?: 'text' | 'search' | 'password';
    /**
     * Component props that are forwarded to the password button. When onClick is provided, the default
     * implementation is disabled.
     */
    passwordButton?: ButtonHTMLAttributes<HTMLButtonElement>;
    /**
     * Component props that are forwarded to the search button
     */
    searchButton?: ButtonHTMLAttributes<HTMLButtonElement>;
    /**
     * Component props that are forwarded to the reset button
     */
    resetButton?: ButtonHTMLAttributes<HTMLButtonElement>;
    /**
     * Only used on "search" type, whether a reset button should be rendered
     */
    withReset?: boolean;
}

interface TextFieldButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    iconName: string;
    baseClassName: string;
}

function TextFieldButton(props: TextFieldButtonProps) {
    const {
        iconName,
        title,
        type = 'button',
        baseClassName,
        className,
        ...rest
    } = props;
    const classes = cn(baseClassName, className);
    return (
        <button type={type} className={classes} {...rest}>
            <Icon isUiIcon iconName={iconName} title={title} />
        </button>
    );
}

/**
 * Renders a text field
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
    (props, ref) => {
        const {
            id,
            label,
            type = 'text',
            withReset,
            className,
            style,
            passwordButton,
            searchButton,
            resetButton,
            ...rest
        } = props;
        const [showPassword, setShowPassword] = useState(false);
        const handlePasswordIconClick = useCallback(() => {
            setShowPassword(prev => !prev);
        }, []);

        const isSearch = type === 'search';
        const isPassword = type === 'password';

        const classes = cn(
            'a-text-field',
            { 'a-text-field--password': isPassword },
            { 'a-text-field--search': isSearch },
            className,
        );

        return (
            <div className={classes} style={style}>
                {label && <label htmlFor={id}>{label}</label>}
                <input
                    id={id}
                    type={showPassword ? 'text' : type}
                    ref={ref}
                    {...rest}
                />
                {isPassword && (
                    <TextFieldButton
                        onClick={handlePasswordIconClick}
                        iconName={showPassword ? 'watch-off' : 'watch-on'}
                        baseClassName="a-text-field__icon-password"
                        {...passwordButton}
                    />
                )}
                {isSearch && (
                    <>
                        <TextFieldButton
                            iconName="search"
                            type="submit"
                            baseClassName="a-text-field__icon-search"
                            {...searchButton}
                        />
                        {withReset && (
                            <TextFieldButton
                                iconName="close-small"
                                baseClassName="a-text-field__icon-close"
                                {...resetButton}
                            />
                        )}
                    </>
                )}
            </div>
        );
    },
);
TextField.displayName = 'TextField';
